// Third-party
import React from 'react';
import moment from 'moment';

// Components
import { EditableInput } from '@/views/components/inputs';
import { Pastille } from '@/views/components/misc';
import ComparisonContactButton from './ComparisonContactButton';

// Hooks
import useScreenSize from '@/hooks/useScreenSize';
import useThemeVersion from '@/hooks/useThemeVersion';

// Types
import Comparison from '@/types/comparison';

// Utils
import { comparisonIsReadOnly } from '@/utils/comparison';
import { formatMessage, FormattedMessage } from '@/utils/i18n';
import { getComparisonTimeSinceSave } from '@/utils/comparison/details';

type Props = {
  comparison: Comparison;
  isSaving: boolean;
  onComparisonNameChange: (name: string) => void;
  redirectToContactPage: () => void;
};

const ComparisonDetailsSection = ({ comparison, isSaving, onComparisonNameChange, redirectToContactPage }: Props) => {
  const { isLightVersion } = useThemeVersion();
  const comparisonDate = comparison.lastRunAt ? moment(comparison.lastRunAt) : null;
  const DEFAULT_COMPARISON_NAME = formatMessage('Comparison.DefaultName');
  const isReadOnly = comparisonIsReadOnly(comparison);
  const { isTablet } = useScreenSize();

  return (
    <div className="mobile-menu-comparison-details">
      <div className="mobile-menu-comparison-details-title">
        <FormattedMessage id="Proposal.Tabs.Options.ComparisonDetails" />
      </div>
      <div className="comparison-details-rows">
        <div className="comparison-details-row">
          <div>
            <FormattedMessage id="Proposal.Tabs.Options.Name" />
          </div>
          <div>
            <EditableInput
              className="comparison-name"
              dark={!isLightVersion}
              data-dd-privacy="mask"
              data-private
              placeholder={formatMessage('Comparison.NamePlaceholder')}
              readOnly={isReadOnly}
              value={comparison.name || DEFAULT_COMPARISON_NAME}
              onValueChange={onComparisonNameChange}
            />
          </div>
        </div>
        <hr />
        <div className="comparison-details-row">
          <div>
            <FormattedMessage id="Proposal.Tabs.Options.Status" />
          </div>
          <div className="comparison-details-status">
            <div className="comparison-time-since-save-container d-flex align-items-center justify-content-center">
              {comparisonDate && (
                <div className="comparison-date">
                  {isReadOnly && !isSaving && (
                    <span className="comparison-is-historical-data">
                      <FormattedMessage id="Comparison.HistoricalData" values={{ date: comparisonDate.format(formatMessage('Common.DateFormats.Short')) }} />
                      {isTablet && '\u00a0·\u00a0'}
                    </span>
                  )}
                </div>
              )}
            </div>
            <div className="comparison-time-since-save">{getComparisonTimeSinceSave(comparison)}</div>
            <div className="comparison-read-only">
              {comparisonDate && isReadOnly && !isSaving && (
                <Pastille className="pastille-read-only">
                  <FormattedMessage id="Common.ReadOnly" />
                </Pastille>
              )}
            </div>
          </div>
        </div>
        <hr />
        <div className="comparison-details-row">
          <div>
            <FormattedMessage id="Proposal.Tabs.Options.Contact" />
          </div>
          <div>
            <ComparisonContactButton comparison={comparison} redirectToContactPage={redirectToContactPage} />
          </div>
        </div>
      </div>
    </div>
  );
};

export default ComparisonDetailsSection;
